// import { Lexend } from "@next/font/google";
import { useState, useRef } from "react";
import Head from "next/head";
import Image from "next/image";
import CtaOne from "@components/CTA/CtaOne";
import Slider from "@components/Slider";
import MiniCards from "@components/Cards/MiniCards";
import { BasicChip, MiniNavbar } from "@components/MiniComponents";
import { MdOutlineElectricBolt } from "react-icons/md";
import FeatureCards from "@components/Cards/FeatureCards";
import { ButtonPrimary } from "@components/Buttons";
import CardWithImage from "@components/Cards/CardWithImage";
import BlogCard from "@components/Blog/BlogCard";
import NavCard from "@components/Cards/NavCard";
import {
  MdOutlineMail,
  MdOutlineFileUpload,
  MdOutlineSecurity,
} from "react-icons/md";
import { LiaFileSignatureSolid } from "react-icons/lia";
import { HiOutlineRefresh } from "react-icons/hi";
import { IoMdRepeat } from "react-icons/io";
import { MdOutlineGroups } from "react-icons/md";
import { IoRocketOutline } from "react-icons/io5";
import { AiOutlineAudit } from "react-icons/ai";
import ClientsTestimonial from "@components/Cards/ClientsTestimonial";
import Subscribe from "@components/Subscribe";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { BASE_ASSETS_URL } from "../../constants/constants";
import Certifications from "@components/Security/Certifications";
import { securityCertifications } from "@constants/certifications";
import Legality from "@components/Legality";
import { createUTMQueryParams } from "@utils/signature_owner";
import CustomersComponent from "components/customers";
import { featuredBlogs } from "@constants/blogs";

// const lexend = Lexend({
//   weight: ["100", "200", "300", "400", "500", "600", "700", "800", "900"],
//   style: ["normal"],
//   subsets: ["latin"],
// });

gsap.registerPlugin(useGSAP, ScrollTrigger);

const customerImages = [
  "company1.png",
  "company2.png",
  "company3.png",
  "company4.png",
  "company5.png",
  "company6.png",
  "company7.png",
];

const miniCardsData = [
  {
    icon: <IoMdRepeat />,
    title: "Streamline Repetitive Operations",
    description:
      "Save time by automating routine tasks for scaling your small business.",
  },
  {
    icon: <IoRocketOutline />,
    title: "Accelerate Decision Making",
    description:
      "Speed up your business contracts for quicker decisions and enhanced agility.",
  },
  {
    icon: <MdOutlineGroups />,
    title: "Enhance Team Collaboration",
    description:
      "Facilitate better teamwork with features designed for easy collaboration.",
  },
];

const SlidingCardsData = {
  Forms: {
    nav: "Forms",
    title:
      "Collect important data and get signatures on forms that ensure compliance",
    description:
      "You can now effortlessly generate a range of business forms, including registration forms, purchase orders, feedback forms, and more, complete with signatures to guarantee legality and security.",
    buttonPrimaryUrl:
      "https://help.boloforms.com/en/articles/15419037-how-to-create-signature-forms",
  },
  PDFs: {
    nav: "PDFs",
    title: "Upload your business contracts and get it signed in a few clicks",
    description:
      "Get your PDFs signed in a secure and professional way to manage agreements with clients, suppliers, and employees.",
    buttonPrimaryUrl:
      "https://help.boloforms.com/en/articles/15431025-how-to-send-signature-request-on-pdf",
  },
  Templates: {
    nav: "Templates",
    title:
      "Customize PDF templates and reuse them anytime to meet your business needs",
    description:
      "Assign roles and reuse the pre-made templates that you created to save time and avoid repetitive tasks.",
    buttonPrimaryUrl:
      "https://help.boloforms.com/en/articles/15420696-how-to-create-a-pdf-template",
  },
};

const featuresData = [
  {
    icon: <MdOutlineMail />,
    title: "Customizable Emails",
    styling: "bg-[#b8f6b7]",
    description:
      "Edit the emails you send with signature requests to match your business’ tone and style, making every interaction professional.",
  },
  {
    icon: <MdOutlineFileUpload />,
    title: "Seamless Upload",
    styling: "bg-[#f5daf6]",
    description:
      "Upload documents quickly and effortlessly, right from your platform, no technical skills needed.",
  },
  {
    icon: <AiOutlineAudit />,
    title: "Audit Trial",
    styling: "bg-[#fee5cd]",
    description:
      "See a detailed record of all actions taken on a document, including who viewed and signed it, for your peace of mind.",
  },
  {
    icon: <MdOutlineSecurity />,
    title: "Secure and Legally Binding",
    styling: "bg-[#fee5cd]",
    description:
      "Protect your business with legally recognized and secure electronic signatures.",
  },
  {
    icon: <LiaFileSignatureSolid />,
    title: "Multi-Recipient Signature Requests",
    styling: "bg-[#b8f6b7]",
    description:
      "Send signature requests to multiple individuals, allowing collaboration and simultaneous signing on a single document.",
  },
  {
    icon: <HiOutlineRefresh />,
    title: "Real-Time Dashboard Updates",
    styling: "bg-[#f5daf6]",
    description:
      "Stay informed with immediate alerts on document progress, seeing which are signed and which are waiting, all from one dashboard.",
  },
];

const whyChooseData = [
  {
    title: "Create Signature Forms",
    description: `Easily add signature fields to your forms using our intuitive form editor. Save time managing 100s of signatures without the hassle of creating individual documents.`,
    image: "choose1.png",
  },
  {
    title: "Team up, without thinking about cost",
    description:
      "Even if we are a platform for small businesses to take e-signatures, we have enterprise-grade security to protect your contracts from potential threats and ensure compliance with international and industry-specific eSignature laws.",
    image: "choose2.png",
  },
  {
    title: "PDF templates for repeating contracts",
    description:
      "When you have a contract that is 95% the same but has some minor changes to be made to send to every new person, avoid the redundancy of recreating and remapping signature fields, customize minor details, and send to new recipients efficiently.",
    image: "choose3.png",
  },
];

const integrationsData = [
  {
    image: "zapier.png",
    title: "Zapier",
    description: "Automate tasks between 6000+ apps.",
  },
  {
    image: "pabbly.png",
    title: "Pabbly",
    description: "Connect apps and automate business processes.",
  },
  {
    image: "make.png",
    title: "Make",
    description: "Create custom workflows across multiple apps.",
  },
  {
    image: "pipedrive.png",
    title: "Pipedrive",
    description: "Streamline sales management and tracking.",
  },
  {
    image: "highlevel.png",
    title: "Highlevel",
    description: "Consolidate marketing efforts into one platform",
  },
  {
    image: "salesforce.png",
    title: "Salesforce",
    description: "Enhance customer relationship management seamlessly.",
  },
];

const integrationsData2 = [
  {
    image: "hubspot.png",
    title: "Hubspot",
    description: "Enhance your marketing and sales data.",
  },
  {
    image: "dynamics.png",
    title: "Ms Dynamics 365",
    description: "Integrate CRM and ERP capabilities.",
  },
  {
    image: "drive.png",
    title: "Google Drive",
    description: "Store and access files from anywhere.",
  },
  {
    image: "slack.png",
    title: "Slack",
    description: "Improve team communication and collaboration.",
  },
  {
    image: "sheets.png",
    title: "Google Sheets",
    description: "Automate data entry and updates.",
  },
  {
    image: "teams.png",
    title: "Microsoft Teams",
    description: "Collaborate and communicate within your team effortlessly.",
  },
];

const Home = () => {
  let container = useRef();
  useGSAP(
    () => {
      let mm = gsap.matchMedia();
      mm.add("(max-width: 834px)", () => {
        let slidingCards = gsap.utils.toArray(".sliding-card");
        slidingCards.forEach((card) => {
          ScrollTrigger.create({
            trigger: card,
            start: "top 81px",
            pin: true,
            pinSpacing: false,
            scrub: true,
          });
        });
      });
      mm.add("(min-width: 835px)", () => {
        let slidingCards = gsap.utils.toArray(".sliding-card");
        slidingCards.forEach((card) => {
          ScrollTrigger.create({
            trigger: card,
            start: "top 97px",
            pin: true,
            pinSpacing: false,
            scrub: true,
          });
        });
      });
    },
    {
      scope: container,
    },
  );
  return (
    <div className={`w-full`} ref={container}>
      <Head>
        <title>BoloSign - AI for Smart Contract Management</title>
        <meta
          name="description"
          content="AI assistant to draft, review, and eSign contracts on autopilot for fastest growing companies."
        />
        <link
          rel="icon"
          href="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/Logo.png"
        />
        <meta name="geo.region" content="IN" />
        <meta name="geo.position" content="22.351115;78.667743" />
        <meta name="ICBM" content="22.351115, 78.667743" />
        <meta name="author" content="BoloSign" />
        <meta name="publisher" content="BoloSign " />
        <meta
          name="robots"
          content="INDEX, FOLLOW, MAX-IMAGE-PREVIEW:LARGE, MAX-SNIPPET:-1, MAX-VIDEO-PREVIEW:-1"
        />

        {/* <!-- Facebook Meta Tags --> */}
        <meta property="og:url" content="https://bolosign.com" />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="BoloSign - AI for Smart Contract Management"
        />
        <meta
          property="og:description"
          content="AI assistant to draft, review, and eSign contracts on autopilot for fastest growing companies."
        />
        <meta
          property="og:image"
          content="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/media-viewport.png"
        />

        {/* <!-- Twitter Meta Tags --> */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="https://bolosign.com" />
        <meta property="twitter:url" content="https://bolosign.com" />
        <meta
          name="twitter:title"
          content="BoloSign - AI for Smart Contract Management"
        />
        <meta
          name="twitter:description"
          content="AI assistant to draft, review, and eSign contracts on autopilot for fastest growing companies."
        />
        <meta
          name="twitter:image"
          content="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/media-viewport.png"
        />

        <link rel="canonical" href="https://www.bolosign.com/signature/" />
      </Head>
      <div className={`w-full`}>
        {/* hero section  */}
        <div className="flex flex-col justify-center items-center gap-14 px-4 md:px-8 pb-32">
          <div className="max-w-screen-lg text-center">
            <CtaOne
              heading1={`AI for Smart Contract Management`}
              paragraph={`AI assistant to draft, review, and eSign contracts on autopilot for fastest growing companies.`}
              paragraphStyling={`max-w-[500px] mx-auto`}
              buttonsStyling={`justify-center`}
              buttonPrimaryText={`Get Started`}
              buttonPrimaryUrl={`https://signature.boloforms.com/register?from=WEBSITE_HERO_BUTTON${createUTMQueryParams(
                {
                  from: "WEBSITE_HERO_BUTTON",
                },
              )}`}
              buttonSecondaryText={`Buy Now`}
              buttonSecondaryUrl={`/signature/pricing`}
              newTab={true}
              signaturePopupOpen={true}
            />
          </div>
          <div className="">
            <Certifications
              certifications={securityCertifications}
              displayText={false}
            />
          </div>
          <div className="w-full mt-14 xl:mt-0 txl:w-[1440px] relative flex justify-center items-center">
            <div className="w-5/6">
              <Image
                src={`${BASE_ASSETS_URL}/v2/arrow.svg`}
                width={10}
                height={10}
                className={`moveUpObject w-[38px] sm:w-[50px] xl:w-[80px] absolute -top-20 xl:-top-40 left-[30%] xl:left-1/4`}
                alt="hero"
              />
              <Image
                src={`${BASE_ASSETS_URL}/v2/wheel.svg`}
                width={10}
                height={10}
                className={`rotateObject w-[38px] sm:w-[50px] xl:w-[60px] absolute -top-20 xl:-top-32 right-[30%] xl:right-1/4`}
                alt="hero"
              />
              {/* center image  */}
              <Image
                src={`${BASE_ASSETS_URL}/v2/landing-page/dashboard.png`}
                width={10}
                height={10}
                className={`rounded-lg w-full imageBorders`}
                alt="hero"
              />
            </div>
            <Image
              src={`${BASE_ASSETS_URL}/v2/landing-page/hero1.png`}
              width={10}
              height={10}
              className={`moveCornerLeftObject rounded-lg w-[80px] xs:w-[100px] sm:w-[140px] md:w-[180px] lg:w-[250px] imageBorders absolute -top-12 sm:-top-20 lg:-top-32 left-0 -z-10 -rotate-12`}
              alt="hero"
            />
            <Image
              src={`${BASE_ASSETS_URL}/v2/landing-page/hero2.png`}
              width={10}
              height={10}
              className={`moveCornerRightObject rounded-lg w-[80px] xs:w-[100px] sm:w-[140px] md:w-[180px] lg:w-[250px] imageBorders absolute -top-12 sm:-top-20 lg:-top-40 right-0 -z-10 rotate-12`}
              alt="hero"
            />
            <Image
              src={`${BASE_ASSETS_URL}/v2/landing-page/hero3.png`}
              width={10}
              height={10}
              className={`moveDownObject rounded-lg w-[80px] xs:w-[100px] sm:w-[140px] md:w-[180px] lg:w-[250px] imageBorders absolute bottom-4 sm:bottom-8 md:bottom-20 left-0 z-10`}
              alt="hero"
            />
            <Image
              src={`${BASE_ASSETS_URL}/v2/landing-page/hero4.png`}
              width={10}
              height={10}
              className={`moveUpObject rounded-lg w-[80px] xs:w-[100px] sm:w-[140px] md:w-[180px] lg:w-[250px] imageBorders absolute bottom-4 sm:bottom-8 md:bottom-16 right-0 z-10`}
              alt="hero"
            />
          </div>
        </div>
        {/* slider  */}
        {/* <div className="w-full">
          <Slider
            heading="5,000+ Companies Use BoloSign Around the World"
            sliderImagesData={customerImages}
            styling={`bg-red-100/75 py-16 border-y-2 border-gray-900`}
          />
        </div> */}
        <CustomersComponent />
        {/* section 3 */}
        <div className="max-w-screen-mxl mx-auto py-20 md:py-32 px-4 md:px-8 xxl:px-0">
          <div className="flex flex-col lg:flex-row items-center justify-between">
            <div className="sm:w-[83%] lg:w-[50%]">
              <CtaOne
                heading2={`Affordable eSignature solution for your team`}
              />
            </div>
            <div className="sm:w-[83%] lg:w-[45%] relative">
              <CtaOne
                paragraph={`Experience how easy document management can be with BoloSign. Our user-friendly platform is designed to help your team work better together, speeding up projects and enhancing productivity.`}
                buttonPrimaryText={`Get Started`}
                buttonPrimaryUrl={`https://signature.boloforms.com/register?from=WEBSITE_HERO_BUTTON${createUTMQueryParams(
                  {
                    from: "WEBSITE_HERO_BUTTON",
                  },
                )}`}
              />
              <Image
                src={`${BASE_ASSETS_URL}/v2/wheel.svg`}
                width={10}
                height={10}
                className={`rotateObject w-[50px] sm:w-[70px] xl:w-[90px] absolute bottom-0 lg:-bottom-20 right-6`}
                alt="hero"
              />
            </div>
          </div>
          <div className="mt-20 w-full flex flex-col lg:flex-row items-center justify-between">
            <div className="md:w-3/4 lg:flex-1 relative">
              <Image
                src={`${BASE_ASSETS_URL}/v2/landing-page/section3.png`}
                width={10}
                height={10}
                className={`rounded-lg w-full`}
                alt="hero"
              />
              <div className="absolute bottom-6 left-6">
                <BasicChip
                  icon={<MdOutlineElectricBolt />}
                  text="Increase Productivity With BoloSign Signature"
                />
              </div>
            </div>
            <div className="md:w-3/4 lg:flex-1 flex justify-center lg:justify-end items-center">
              <MiniCards
                cardsData={miniCardsData}
                cardsStyling={
                  "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6 lg:gap-10"
                }
              />
            </div>
          </div>
        </div>
        {/* legality  */}
        <div className="w-full max-w-screen-mxl mb-28 sm:mb-40 mx-auto px-4 md:px-8 xxl:px-0">
          <Legality />
        </div>
        {/* section 4 */}
        <div className="max-w-screen-mxl pb-20 mx-auto px-4 md:px-8 xxl:px-0 flex flex-col gap-4 sm:gap-10">
          <div className="text-center max-w-screen-lg mx-auto">
            <CtaOne
              heading2={`Why BoloSign Is the Best Solution for Your Small Business`}
              paragraph={`We understand that streamlined document management is essential for maximizing productivity and ensuring the success of your projects.`}
              paragraphStyling={`max-w-screen-sm mx-auto`}
            />
          </div>
          <div className="sliding-cards relative w-full flex flex-col gap-20">
            <div className="sliding-card z-10 bg-white w-full sm:w-5/6 lg:w-full mx-auto flex flex-col lg:flex-row justify-between border border-gray-600 rounded-lg">
              <div className="w-full lg:w-1/2">
                <NavCard activeNav={`Forms`} cardsData={SlidingCardsData} />
              </div>
              <div className="h-[370px] xxs:h-[400px] sm:h-[550px] lg:h-auto w-full lg:w-1/2 relative bg-sky-200">
                <Image
                  src={`${BASE_ASSETS_URL}/v2/landing-page/forms3.png`}
                  width={10}
                  height={10}
                  className={`moveRightObject rounded-lg w-auto h-[19%] absolute z-10 top-[12%] left-[10%]`}
                  alt="hero"
                />
                <Image
                  src={`${BASE_ASSETS_URL}/v2/landing-page/forms1.png`}
                  width={10}
                  height={10}
                  className={`moveCornerTopObject rounded-lg w-auto h-[65%] xxs:h-[70%] xs:h-[80%] lg:h-[70%] imageBorders absolute z-10 top-[10%] right-[10%]`}
                  alt="hero"
                />
                <Image
                  src={`${BASE_ASSETS_URL}/v2/landing-page/forms2.png`}
                  width={10}
                  height={10}
                  className={`moveCornerBottomObject rounded-lg w-auto h-[50%] xxs:h-[50%] xs:h-[60%] lg:h-[50%] imageBorders absolute left-[10%] bottom-[10%]`}
                  alt="hero"
                />
              </div>
            </div>
            <div className="sliding-card z-10 bg-white w-full sm:w-5/6 lg:w-full mx-auto flex flex-col-reverse lg:flex-row-reverse justify-between border border-gray-600 rounded-lg">
              <div className="w-full lg:w-1/2">
                <NavCard activeNav={`PDFs`} cardsData={SlidingCardsData} />
              </div>
              <div className="bg-purple-200 h-[370px] xxs:h-[400px] sm:h-[550px] lg:h-auto w-full lg:w-1/2 relative">
                <Image
                  src={`${BASE_ASSETS_URL}/v2/landing-page/pdf4.png`}
                  width={10}
                  height={10}
                  className={`rounded-lg w-auto h-[50%] xs:h-[60%] imageBorders absolute z-10 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2`}
                  alt="hero"
                />
                <Image
                  src={`${BASE_ASSETS_URL}/v2/landing-page/pdf3.png`}
                  width={10}
                  height={10}
                  className={`moveRightAndCenterObject rounded-lg w-auto h-[30%] lg:h-[35%] imageBorders absolute left-[5%] top-1/2 z-10`}
                  alt="hero"
                />
                <Image
                  src={`${BASE_ASSETS_URL}/v2/landing-page/pdf1.png`}
                  width={10}
                  height={10}
                  className={`moveUpObject rounded-lg w-auto h-[40%] imageBorders absolute right-[5%] top-[10%] z-10`}
                  alt="hero"
                />
                <Image
                  src={`${BASE_ASSETS_URL}/v2/landing-page/pdf2.png`}
                  width={10}
                  height={10}
                  className={`moveDownObject rounded-lg w-auto h-[40%] imageBorders absolute right-[5%] bottom-[8%] z-10`}
                  alt="hero"
                />
              </div>
            </div>
            <div className="sliding-card1 z-10 bg-white w-full sm:w-5/6 lg:w-full mx-auto flex flex-col lg:flex-row justify-between border border-gray-600 rounded-lg">
              <div className="w-full lg:w-1/2">
                <NavCard activeNav={`Templates`} cardsData={SlidingCardsData} />
              </div>
              <div className="bg-red-100 h-[370px] xxs:h-[400px] sm:h-[550px] lg:h-auto w-full lg:w-1/2 relative">
                <Image
                  src={`${BASE_ASSETS_URL}/v2/landing-page/template2.png`}
                  width={10}
                  height={10}
                  className={`rounded-lg w-auto h-[50%] xs:h-[70%] imageBorders absolute z-10 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2`}
                  alt="hero"
                />
                <Image
                  src={`${BASE_ASSETS_URL}/v2/landing-page/template3.png`}
                  width={10}
                  height={10}
                  className={`moveUpObject rounded-lg w-auto h-[30%] lg:h-[40%] imageBorders absolute left-[5%] xs:left-[5%] bottom-[10%] z-10`}
                  alt="hero"
                />
                <Image
                  src={`${BASE_ASSETS_URL}/v2/landing-page/template1.png`}
                  width={10}
                  height={10}
                  className={`moveDownObject rounded-lg w-auto h-[40%] lg:h-[50%] imageBorders absolute right-[5%] top-[5%] z-10`}
                  alt="hero"
                />
              </div>
            </div>
          </div>
        </div>
        {/* features  */}
        <div className="border-y-2 border-gray-700 bg-[#d7f3fe] py-20 mx-auto px-4 md:px-8 xxl:px-0 flex flex-col gap-4 sm:gap-10">
          <div className="text-center max-w-screen-lg mx-auto">
            <CtaOne
              heading2={`As a Company, We Offer Our Customers Cool Features`}
              paragraph={`We're dedicated to delivering exceptional results, fostering lasting relationships, and ensuring our clients' success in an ever competitive digital marketplace.`}
              paragraphStyling={`max-w-screen-sm mx-auto`}
            />
          </div>
          <div className="max-w-screen-mxl mx-auto">
            {
              <FeatureCards
                cardsData={featuresData}
                featuresStyling={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10`}
              />
            }
            <div className="mt-10 flex justify-center items-center">
              <ButtonPrimary buttonText={`All Features`} buttonUrl={`#`} />
            </div>
          </div>
        </div>
        {/* why choose  */}
        <div className="max-w-screen-mxl py-20 lg:py-28 mx-auto px-4 md:px-8 xxl:px-0 flex flex-col gap-4 sm:gap-10">
          <div className="w-full sm:w-[80%] md:w-[70%] lg:w-full mx-auto flex flex-col justify-center items-center gap-12 sm:gap-28">
            <div className="w-full flex flex-col-reverse lg:flex-row justify-center items-center gap-20">
              <div className="flex-1">
                <CardWithImage
                  title={whyChooseData[0].title}
                  description={whyChooseData[0].description}
                  image={whyChooseData[0].image}
                />
              </div>
              <div className="flex-1 relative">
                <CtaOne
                  heading2={`BoloSign - Why Choose Us`}
                  paragraph={`We allow you to efficiently manage document workflows, send multi-recipient signatures, and receive real-time updates, significantly streamlining your operations. Each transaction is secure and legally compliant, backed by robust security features and a detailed audit trail.`}
                  buttonPrimaryText={`Watch Trailer`}
                  buttonPrimaryUrl={`https://www.youtube.com/watch?v=HE4oDN_T12Q`}
                />
                <Image
                  src={`${BASE_ASSETS_URL}/v2/wheel.svg`}
                  width={10}
                  height={10}
                  className={`rotateObject w-[50px] sm:w-[70px] xl:w-[90px] absolute bottom-0 lg:-bottom-20 right-6`}
                  alt="hero"
                />
              </div>
            </div>
            <div className="w-full flex flex-col lg:flex-row justify-center gap-12 sm:gap-20">
              <CardWithImage
                title={whyChooseData[1].title}
                description={whyChooseData[1].description}
                image={whyChooseData[1].image}
              />
              <CardWithImage
                title={whyChooseData[2].title}
                description={whyChooseData[2].description}
                image={whyChooseData[2].image}
              />
            </div>
            <div className="w-full flex flex-col lg:flex-row justify-center items-center gap-20">
              <div className="flex-1 relative">
                <CtaOne
                  heading2={`With the only eSignature Solution made for Small Businesses`}
                  paragraph={`We understand the needs of your small business. That’s why we provide a centralized platform that enhances the efficiency of signatures in contracts, agreements, and other legal documents crucial to your business operations at a very affordable price.`}
                  buttonPrimaryText={`Get Started`}
                  buttonPrimaryUrl={`https://signature.boloforms.com/register?from=WEBSITE_HERO_BUTTON${createUTMQueryParams(
                    {
                      from: "WEBSITE_HERO_BUTTON",
                    },
                  )}`}
                />
                <Image
                  src={`${BASE_ASSETS_URL}/v2/wheel.svg`}
                  width={10}
                  height={10}
                  className={`rotateObject w-[50px] sm:w-[70px] xl:w-[90px] absolute bottom-0 lg:-bottom-20 right-6`}
                  alt="hero"
                />
              </div>
              <div className="flex-1 relative w-[90%] sm:w-full">
                <Image
                  src={`${BASE_ASSETS_URL}/v2/landing-page/only_solution1.png`}
                  width={10}
                  height={10}
                  className={`rounded-lg w-full`}
                  alt="hero"
                />
                <Image
                  src={`${BASE_ASSETS_URL}/v2/landing-page/only_solution2.png`}
                  width={10}
                  height={10}
                  className={`imageBorders moveDownObject rounded-lg absolute -left-5 sm:-left-10 bottom-10 w-[130px] sm:w-[200px]`}
                  alt="hero"
                />
              </div>
            </div>
          </div>
        </div>
        {/* integrations  */}
        <div className="border-y-2 border-gray-700 bg-red-100 py-20 lg:py-40 mx-auto px-4 md:px-8 xxl:px-0 flex flex-col gap-4 sm:gap-8">
          <div className="text-center max-w-screen-md mx-auto">
            <CtaOne
              heading2={`Integrate Some of Your Favorite Software`}
              paragraph={`Connect with your CRM, email marketing platform, and more to simplify your business processes.`}
              paragraphStyling={`max-w-screen-sm mx-auto`}
            />
          </div>
          <div className="">
            <Slider cardsData={integrationsData} />
          </div>
          <div className="">
            <Slider cardsData={integrationsData2} direction="right" />
          </div>
        </div>
        {/* clients  */}
        <div className="pt-20 lg:pt-28 flex flex-col gap-6 relative max-w-screen-mxl mx-auto px-4 md:px-8 xxl:px-0">
          <ClientsTestimonial />
        </div>
        {/* articles  */}
        <div className="max-w-screen-mxl mx-auto px-4 md:px-8 xxl:px-0 py-20 lg:py-28 flex flex-col gap">
          <div className="text-center max-w-screen-md mx-auto">
            <CtaOne
              heading2={`Take a Look at Our Featured Articles`}
              paragraph={`These articles will guide you on how to simplify office work, boost your efficiency, and concentrate on expanding your business.`}
              paragraphStyling={`max-w-[600px] mx-auto`}
            />
          </div>
          <div className="mt-10 flex flex-col md:flex-row gap-6 sm:max-w-[80%] md:max-w-full mx-auto">
            {featuredBlogs.map((card, index) => (
              <BlogCard
                key={index}
                {...card}
                imageWidth="w-full md:h-[290px] xl:h-[330px]"
              />
            ))}
          </div>
        </div>
        {/* subscribe  */}
        {/* <div className="border-y-2 border-gray-700 bg-green-100 py-20 lg:py-40 mx-auto px-4 md:px-8 xxl:px-0">
          <div className="max-w-screen-mxl mx-auto flex flex-col lg:flex-row gap-10">
            <div className="flex-1">
              <CtaOne
                heading2={`Show Your Progress With BoloSign`}
                paragraph={`BoloSign management can facilitated through that’s various tools and organize and complete tasks more efficiently.`}
              />
              <div className="flex flex-col sm:flex-row gap-6">
                <input
                  type="text"
                  name=""
                  id=""
                  className="border border-gray-700 rounded-md outline-none px-4 text-gray-800 w-full xs:w-[380px] h-[52px] bg-white placeholder-gray-800"
                  placeholder="Enter Email Address"
                />
                <ButtonPrimary
                  buttonText={`Subscribe`}
                  buttonUrl={`#`}
                  borderShadow={false}
                  icon={false}
                />
              </div>
            </div>
            <div className="w-full h-[500px] lg:h-auto lg:flex-1 relative">
              <Image
                src={`https://assets-global.website-files.com/65dd9f1fc1354b976d7c7cea/65e06692acf60fee33928397_newsletter-image-one.jpg`}
                width={10}
                height={10}
                className={`moveRightAndCenterObject rounded-lg w-auto h-[50%] xs:h-[50%] sm:h-[70%] xl:h-[80%] imageBorders absolute left-[10%] top-1/2 z-10`}
                alt="hero"
              />
              <Image
                src={`https://assets-global.website-files.com/65dd9f1fc1354b976d7c7cea/65e0669b8333a5223d346ea1_analytics.jpg`}
                width={10}
                height={10}
                className={`moveCornerLeftRotateLeftObject rounded-lg w-auto h-[30%] xs:h-[40%] sm:h-[50%] lg:h-[40%] xl:h-[65%] imageBorders absolute right-[5%] sm:right-[15%] md:right-[20%] top-0 lg:top-[-10%] z-10`}
                alt="hero"
              />
              <Image
                src={`https://assets-global.website-files.com/65dd9f1fc1354b976d7c7cea/65e06bcb12defb260901d8e1_project-priority.jpg`}
                width={10}
                height={10}
                className={`moveCornerLeftRotateRightObject rounded-lg w-auto h-[40%] xs:h-[50%] sm:h-[60%] lg:h-[50%] xl:h-[70%] imageBorders absolute right-[10%] sm:right-[30%] bottom-0 xl:bottom-[-20%] z-10`}
                alt="hero"
              />
            </div>
          </div>
        </div> */}
        {/* <div className="w-full">
          <Subscribe />
        </div> */}
      </div>
    </div>
  );
};

export default Home;
